import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import { TriggerExecutionFormState } from './types';
import {
  validateBranchName,
  validateCommitHash,
  validateSubstitutions,
  validateTag,
} from './utils';
import { TriggerPayload } from '@internal/backstage-plugin-cloudbuild-common';

export const TriggerExecutionDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  trigger?: TriggerPayload;
  onExecute: (triggerExecutionFormState: TriggerExecutionFormState) => void;
  isExecuting: boolean;
  defaultBranch: string;
}> = ({ open, onClose, trigger, onExecute, isExecuting, defaultBranch }) => {
  const [triggerExecutionFormState, setTriggerExecutionFormState] =
    useState<TriggerExecutionFormState>({
      branchName: defaultBranch,
      commitHash: '',
      tagName: '',
      revisionType: 'branch',
      substitutions: [{ key: '', value: '' }],
    });
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof TriggerExecutionFormState, string>>
  >({});

  useEffect(() => {
    if (trigger) {
      setTriggerExecutionFormState({
        branchName: defaultBranch,
        commitHash: '',
        tagName: '',
        revisionType: 'branch',
        substitutions: Object.entries(trigger.substitutions ?? {}).map(
          ([key, value]) => ({ key, value }),
        ),
      });
    }
  }, [defaultBranch, trigger, trigger?.id]);

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof TriggerExecutionFormState, string>> = {};

    if (
      triggerExecutionFormState.revisionType === 'branch' &&
      !validateBranchName(triggerExecutionFormState.branchName)
    ) {
      errors.branchName = 'Invalid branch name';
    }

    if (
      triggerExecutionFormState.revisionType === 'commit' &&
      !validateCommitHash(triggerExecutionFormState.commitHash)
    ) {
      errors.commitHash =
        'Invalid commit hash: cloudbuild requires strictly full sha-1 40 characters long and contain only hexadecimal characters (0-9, a-f).';
    }

    if (
      triggerExecutionFormState.revisionType === 'tag' &&
      !validateTag(triggerExecutionFormState.tagName)
    ) {
      errors.tagName = 'Invalid tag name';
    }

    const validationResult = validateSubstitutions(
      triggerExecutionFormState.substitutions,
    );
    if (!validationResult.isValid) {
      errors.substitutions = validationResult.errors ?? undefined;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onExecute(triggerExecutionFormState);
    }
  };

  const handleAddSubstitution = () => {
    setTriggerExecutionFormState(prev => ({
      ...prev,
      substitutions: [...prev.substitutions, { key: '', value: '' }],
    }));
  };

  const handleRemoveSubstitution = (index: number) => {
    setTriggerExecutionFormState(prev => ({
      ...prev,
      substitutions: prev.substitutions.filter((_, i) => i !== index),
    }));
  };

  const handleSubstitutionChange = (
    index: number,
    field: 'key' | 'value',
    value: string,
  ) => {
    setTriggerExecutionFormState(prev => ({
      ...prev,
      substitutions: prev.substitutions.map((sub, i) =>
        i === index ? { ...sub, [field]: value } : sub,
      ),
    }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-label="Run trigger drawer"
    >
      <Box p={3} width={400} role="form" aria-label="Trigger execution form">
        <Typography variant="h6" gutterBottom>
          Run Trigger
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          {trigger?.name}
        </Typography>

        {trigger?.github && (
          <Typography variant="body2" gutterBottom>
            Repository:{' '}
            <Link
              href={`https://github.com/${trigger.github.owner}/${trigger.github.name}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="primary"
              aria-label={`Visit ${trigger.github.owner}/${trigger.github.name} on GitHub`}
            >
              {`${trigger.github.owner}/${trigger.github.name}`}
            </Link>
          </Typography>
        )}

        <Box my={2}>
          <RadioGroup
            aria-label="revision type selection"
            name="revisionType"
            value={triggerExecutionFormState.revisionType}
            onChange={e =>
              setTriggerExecutionFormState(prev => ({
                ...prev,
                revisionType: e.target.value as 'branch' | 'commit' | 'tag',
              }))
            }
          >
            <FormControlLabel
              value="branch"
              control={<Radio />}
              label="Branch"
            />
            <FormControlLabel
              value="commit"
              control={<Radio />}
              label="Commit hash"
            />
            <FormControlLabel
              value="tag"
              control={<Radio />}
              label="Tag name"
            />
          </RadioGroup>
        </Box>

        {/* Existing revision type fields */}
        {triggerExecutionFormState.revisionType === 'branch' && (
          <TextField
            fullWidth
            label="Branch"
            value={triggerExecutionFormState.branchName}
            onChange={e =>
              setTriggerExecutionFormState(prev => ({
                ...prev,
                branchName: e.target.value,
              }))
            }
            error={!!formErrors.branchName}
            helperText={formErrors.branchName}
            margin="normal"
            variant="outlined"
            aria-label="Branch name input"
          />
        )}
        {triggerExecutionFormState.revisionType === 'commit' && (
          <TextField
            fullWidth
            label="Commit hash"
            value={triggerExecutionFormState.commitHash}
            onChange={e =>
              setTriggerExecutionFormState(prev => ({
                ...prev,
                commitHash: e.target.value,
              }))
            }
            error={!!formErrors.commitHash}
            helperText={formErrors.commitHash}
            margin="normal"
            variant="outlined"
            aria-label="Commit hash input"
          />
        )}
        {triggerExecutionFormState.revisionType === 'tag' && (
          <TextField
            fullWidth
            label="Tag"
            value={triggerExecutionFormState.tagName}
            onChange={e =>
              setTriggerExecutionFormState(prev => ({
                ...prev,
                tagName: e.target.value,
              }))
            }
            error={!!formErrors.tagName}
            helperText={formErrors.tagName}
            margin="normal"
            variant="outlined"
            aria-label="Tag name input"
          />
        )}

        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            Substitutions
          </Typography>
          {triggerExecutionFormState.substitutions.map((sub, index) => (
            <Box key={index} display="flex" alignItems="center" mb={2}>
              <TextField
                label="Key"
                value={sub.key}
                onChange={e =>
                  handleSubstitutionChange(index, 'key', e.target.value)
                }
                placeholder="_KEY"
                size="small"
                variant="outlined"
                aria-label={`Substitution key ${index + 1}`}
              />
              <TextField
                label="Value"
                value={sub.value}
                onChange={e =>
                  handleSubstitutionChange(index, 'value', e.target.value)
                }
                placeholder="value"
                size="small"
                variant="outlined"
                aria-label={`Substitution value ${index + 1}`}
              />
              <IconButton
                onClick={() => handleRemoveSubstitution(index)}
                disabled={triggerExecutionFormState.substitutions.length === 0}
                aria-label={`Remove substitution ${index + 1}`}
              >
                <Remove />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            onClick={handleAddSubstitution}
            variant="outlined"
            size="small"
            aria-label="Add substitution"
          >
            Add Substitution
          </Button>
          {formErrors.substitutions && (
            <Typography color="error" variant="caption" display="block">
              {formErrors.substitutions}
            </Typography>
          )}
        </Box>

        <Box mt={3}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={isExecuting}
            fullWidth
            aria-label="Run trigger"
          >
            {isExecuting ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <Box ml={1}>Running...</Box>
              </>
            ) : (
              'Run'
            )}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
